import React from 'react';
import styles from './Icon.module.css';
import safeGlob from '../../../utils/safeGlob';

/*
 * Social icons — brand marks (LinkedIn variants / Twitter / Youtube).
 *
 * Theme-binding convention: when an asset SVG uses `#CCCCCC` as its primary
 * fill, that color is transformed to `currentColor` at registry-build time so
 * the icon inherits the parent's CSS `color` value (matching Icon.Standard's
 * cascade). This lets the SAME SVG file render at any color the consumer sets
 * — no asset edit required. Other hex values in the SVG (e.g. `stroke="white"`
 * on a decorative border) are preserved verbatim.
 *
 * If a future social asset needs multi-color brand fidelity, author the SVG
 * with hex values OTHER than `#CCCCCC` for the brand colors; those will be
 * preserved.
 *
 * Uses `?raw` for the same reason as Icon.Standard (visual regression harness Vite
 * doesn't load instance plugins).
 */
const socialModules = safeGlob<string>(
  '../../../assets/icons/social/*.svg',
  { eager: true, query: '?raw', import: 'default' },
);

const registry: Record<string, string> = {};
for (const [filepath, raw] of Object.entries(socialModules)) {
  const name = filepath.split('/').pop()!.replace(/\.svg$/, '');
  // Theme-bind: replace the convention color (#CCCCCC, case-insensitive) with
  // currentColor so the SVG fill cascades from the parent's CSS `color`.
  registry[name] = raw.replace(/#CCCCCC/gi, 'currentColor');
}

/** Names in the social registry. Adding a new social mark: append to this
 *  array and `SocialIconName` updates automatically — consumers using the
 *  satisfies-check (e.g. Link.tsx) will fail to compile if they fall behind. */
export const SOCIAL_ICON_NAMES = [
  'linkedin',
  'linkedin-small',
  'linkedin-small-txt',
  'twitter',
  'youtube',
] as const;

export type SocialIconName = typeof SOCIAL_ICON_NAMES[number];

export interface SocialProps {
  name: SocialIconName;
  /** Required for social marks — they ARE informative content (brand link).
   *  Omit only if the social icon is purely decorative (rare). */
  title?: string;
  className?: string;
}

export const Social: React.FC<SocialProps> = ({ name, title, className }) => {
  const svg = registry[name];

  if (!svg) {
    if (typeof console !== 'undefined') {
      console.warn(`[Icon.Social] missing asset: social/${name}.svg`);
    }
    return null;
  }

  const wrapperClass = [styles.icon, className].filter(Boolean).join(' ');
  const a11y = title
    ? { role: 'img' as const, 'aria-label': title }
    : { 'aria-hidden': true as const };

  return (
    <span
      className={wrapperClass}
      {...a11y}
      // Safe: SVG sources resolved at build time via import.meta.glob({ as: 'raw' }).
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
};

Social.displayName = 'Icon.Social';
